/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTSVG, TaxType} from '../../../../helpers'
import {KTCard, KTCardBody, InputPrice, Select, Loading, Datepicker} from '../../../../components'
import {PageTitle} from '../../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {staffGetAll, projectLookup, basicLookup} from '../../../../redux/slices/lookup.slice'
import {
  expenseinvoiceFastGetId,
  expenseinvoiceFastUpdate,
  setIsRedirect,
} from '../../../../redux/slices/expenseinvoice.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  staffLookup: state.lookup.staffLookup,
  InvoiceKind: state.lookup.InvoiceKind,
  loading: state.lookup.loading
})

const selectExpenseInvoiceData = (state) => ({
  isRedirect: state.expenseinvoice.isRedirect,
  detail: state.expenseinvoice.detail,
  loading: state.expenseinvoice.loading
})

function Edit() {
  const [tax, setTax] = useState(TaxType[3].id)
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {projectLookupList, staffLookup, InvoiceKind, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectExpenseInvoiceData)
  const dispatch = useDispatch()
  
  const formikRef = useRef(null)
  const isFormPopulatedRef = useRef(false)

  useEffect(() => {
    dispatch(staffGetAll())
    dispatch(projectLookup())
    dispatch(expenseinvoiceFastGetId({id}))
    dispatch(basicLookup('InvoiceKind'))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    InvoiceKindId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    ProjectId: null,
    EditDate: new Date(),
    DefermentDate: new Date(),
    SubTotalPrice: 0,
    ExpenseFile: null,
    InvoiceNumber: '',
    RelatedStaffId: '',
    TotalTax: 0,
    TotalPrice: 0,
    InvoiceKindId: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(expenseinvoiceFastUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    formikRef.current = formik
  }, [formik])

  useEffect(() => {
    if (detail && formikRef.current && !isFormPopulatedRef.current) {
      for (const property in detail) {
        const propVal = property.charAt(0).toUpperCase() + property.slice(1)
        for (const values in formValues) {
          if (values === propVal) {
            formikRef.current.setFieldValue(
              propVal,
              property === 'editDate' ? new Date(detail[property]) : detail[property]
            )
            formikRef.current.setFieldValue(
              propVal,
              property === 'defermentDate' ? new Date(detail[property]) : detail[property]
            )
          }
        }
      }
      if (detail) {
        const taxPerc = Math.round((Number(detail && detail.totalTax) / Number(detail && detail.subTotalPrice)) * 100)
        setTax(TaxType && TaxType.find((item) => item.value == taxPerc) && TaxType.find((item) => item.value == taxPerc).id)
      }
      isFormPopulatedRef.current = true
    }
  }, [detail, formValues])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.EXPENSES'}),
      path: '/expenses/expenses',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/expenses/expenses/fast/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/detail/${id}`)
      dispatch(setIsRedirect(false))
    }
  }, [isRedirect, navigate, pageUrl, id, dispatch])

  useEffect(() => {
    const subTotal =
      formik.values.TotalPrice / (1 + Number(TaxType.find((item) => item.id == tax) && TaxType.find((item) => item.id == tax).value) / 100)
    const totalTax = formik.values.TotalPrice - subTotal

    formik.setFieldValue('SubTotalPrice', Number(subTotal.toFixed(4)))
    formik.setFieldValue('TotalTax', Number(totalTax.toFixed(4)))
  }, [tax, formik.values.TotalPrice])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.EXPENSES.FAST.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.COMPANYID'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <span className='fw-bold fs-6'>{detail.companyName}</span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.PROJECT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={projectLookupList}
                    required
                    {...formik.getFieldProps('ProjectId')}
                    onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
                  />
                  {formik.touched.ProjectId && formik.errors.ProjectId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProjectId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICEKINDNAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={InvoiceKind}
                    required
                    {...formik.getFieldProps('InvoiceKindId')}
                    onChange={(item) => formik.setFieldValue('InvoiceKindId', Number(item && item.value))}
                  />
                  {formik.touched.InvoiceKind && formik.errors.InvoiceKind && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.InvoiceKind}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.EDITDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('EditDate')}
                    onChange={([date]) => formik.setFieldValue('EditDate', date)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.DEFERMENTDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('DefermentDate')}
                    onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.RELATEDSTAFF'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={staffLookup}
                    {...formik.getFieldProps('RelatedStaffId')}
                    onChange={(item) => formik.setFieldValue('RelatedStaffId', Number(item && item.value))}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.TOTALPRICE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('TotalPrice')}
                    onChange={(value) => formik.setFieldValue('TotalPrice', Number(value))}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.TOTALTAX'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <div className='input-group'>
                    <input
                      readOnly
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0 w-75'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('TotalTax')}
                    />
                    <Select
                      data={TaxType}
                      value={tax}
                      onChange={(item) => setTax(Number(item.value))}
                    />
                  </div>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.INVOICENUMBER'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('InvoiceNumber')}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.FILE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <label
                    htmlFor='ExpenseFile'
                    className='btn btn-outline btn-outline-dashed btn-outline-dark btn-active-light-dark position-relative overflow-hidden'
                  >
                    <input
                      className='position-absolute z-index-2 opacity-0 w-1px h-1px'
                      type='file'
                      accept='image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg'
                      id={'ExpenseFile'}
                      name={'ExpenseFile'}
                      onChange={(event) =>
                        formik.setFieldValue('ExpenseFile', event.currentTarget.files[0])
                      }
                    />
                    <KTSVG
                      path='/images/icons/duotune/communication/com008.svg'
                      className='svg-icon-3 position-relative z-index-1'
                    />
                    {intl.formatMessage({id: 'EXPENSEINVOICE.UPDATEFAST.FILE'})}
                  </label>
                </div>
              </div>

              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() =>
                      navigate(`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/detail/${id}`)
                    }
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}.{' '}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
